// Station side of production (M5, plan §9.1, §9.5; D-024, D-058): what this station did about
// each batch of the production queue, and what the server answered.
//
// A batch action (`batch.start`, `batch.complete`, `batch.cancel`) is a command in the outbox;
// in the same transaction a local-only `batch_action` row records it (`pending`). The
// uploader writes the server's final answer onto the row (`applied`, `applied_with_flags`,
// `held`, `rejected`, with the answer's data and flags), so the queue never shows a batch as
// done before the server says so, and still shows it after it leaves the `production` stream
// (completed and cancelled batches drop out; the row keeps what the queue needs).
//
// Exactly once from the station too: a second action of the same kind on the same batch
// (a double tap, two people at one tablet) is refused inside the transaction, before
// anything is queued, unless the first was refused by the server.

import type { CommandResponse } from '@enp/domain';
import type { LocalEffect, LocalReader, LocalTx } from './engine.js';
import type { HeldResolution } from './held-actions.js';

/** What was done about a batch. */
export type BatchActionKind = 'start' | 'complete' | 'cancel';

/** Where a batch action stands: `pending` until the server's final answer. */
/** A held one becomes `booked` or `voided` once a manager resolves it (CR8B-4). */
export type BatchActionStatus =
  'pending' | 'applied' | 'applied_with_flags' | 'held' | 'rejected' | 'booked' | 'voided';

/** The batch as the station knew it when it acted (kept after it leaves the stream). */
export interface BatchSnapshot {
  number: string;
  product_id: string;
  planned_qty: string;
  recipe_version_id: string;
  planned_start: string | null;
  planned_day: string | null;
  origin: string | null;
  started_at: string | null;
}

/** The output lot made on the station (its id and `L-…` code, so the label prints offline). */
export interface OutputLotDetail {
  lot_id: string;
  lot_code: string;
  /** `YYYY-MM-DD`, or null when the product has no best-before. */
  best_before: string | null;
  /** The state printed on the label (the product's first state). */
  state: string;
}

/** What a completion recorded. */
export interface BatchCompleteDetail {
  actual_yield_qty: string;
  input_qty?: string;
  output_lot: OutputLotDetail;
  /** Component lots the baker scanned (the rest were left to FEFO). */
  component_lots: { product_id: string; lot_id: string; lot_code: string | null }[];
}

/** What a cancellation recorded. */
export interface BatchCancelDetail {
  reason: string;
}

/** The server's final answer, as kept on the row. */
export interface BatchAnswer {
  data?: Record<string, unknown>;
  flags: { code: string; message?: string; data?: Record<string, unknown> }[];
  reason?: string;
  message?: string;
  hold_id?: string;
  /** A manager's resolution of the hold (CR8B-4). */
  resolution?: HeldResolution;
}

/** A `batch_action` row as stored (JSON columns as text). */
export interface BatchActionRow {
  id: string;
  batch_id: string;
  action: BatchActionKind;
  staff_id: string | null;
  at: string;
  day: string;
  batch: string | null;
  detail: string | null;
  status: BatchActionStatus;
  answer: string | null;
  answered_at: string | null;
}

/** This batch already has that action on this station (a double tap, a second person). */
export class BatchAlreadyRecordedError extends Error {
  constructor(
    readonly batchId: string,
    readonly action: BatchActionKind,
  ) {
    super(`This station already recorded "${action}" for batch ${batchId}`);
    this.name = 'BatchAlreadyRecordedError';
  }
}

/** The action already recorded for `batchId` (any status but `rejected`), if any. */
export function findBatchAction(
  db: LocalReader,
  batchId: string,
  action: BatchActionKind,
): Promise<BatchActionRow | null> {
  return db.getOptional<BatchActionRow>(
    `SELECT * FROM batch_action WHERE batch_id = ? AND action = ? AND status != 'rejected'
      ORDER BY at DESC LIMIT 1`,
    [batchId, action],
  );
}

/**
 * Records what was done about a batch, in the transaction of the command that does it.
 * Throws {@link BatchAlreadyRecordedError} (rolling the command back) when the same action
 * is already recorded for the batch and the server hasn't refused it.
 */
export async function recordBatchAction(
  tx: LocalTx,
  row: {
    commandId: string;
    batchId: string;
    action: BatchActionKind;
    staffId: string;
    at: string;
    day: string;
    batch: BatchSnapshot;
    detail?: BatchCompleteDetail | BatchCancelDetail;
  },
): Promise<void> {
  if (await findBatchAction(tx, row.batchId, row.action)) {
    throw new BatchAlreadyRecordedError(row.batchId, row.action);
  }
  await tx.execute(
    `INSERT INTO batch_action (id, batch_id, action, staff_id, at, day, batch, detail, status, answer, answered_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?, 'pending', NULL, NULL)`,
    [
      row.commandId,
      row.batchId,
      row.action,
      row.staffId,
      row.at,
      row.day,
      JSON.stringify(row.batch),
      row.detail === undefined ? null : JSON.stringify(row.detail),
    ],
  );
}

/** A command's local effect that records the batch action (for `StationData.enqueue`). */
export function batchActionEffect(
  action: BatchActionKind,
  options: {
    batchId: string;
    day: string;
    batch: BatchSnapshot;
    detail?: BatchCompleteDetail | BatchCancelDetail;
  },
): LocalEffect {
  return (tx, envelope) =>
    recordBatchAction(tx, {
      commandId: envelope.command_id,
      batchId: options.batchId,
      action,
      staffId: envelope.staff_id,
      at: envelope.occurred_at,
      day: options.day,
      batch: options.batch,
      ...(options.detail ? { detail: options.detail } : {}),
    });
}

/**
 * Writes a final answer onto its batch action (called by the uploader for every final
 * answer; a command that isn't a batch action has no row and nothing changes). Transient
 * answers never reach here: the command stays `pending` and is sent again.
 */
export async function recordBatchAnswer(
  tx: LocalTx,
  answer: CommandResponse,
  now: Date,
): Promise<void> {
  const id = answer.command_id;
  if (!id) return;
  if (
    answer.status !== 'applied' &&
    answer.status !== 'applied_with_flags' &&
    answer.status !== 'held' &&
    answer.status !== 'rejected'
  ) {
    return;
  }
  const kept: BatchAnswer = {
    flags: 'flags' in answer && Array.isArray(answer.flags) ? answer.flags : [],
    ...('data' in answer && answer.data ? { data: answer.data } : {}),
    ...('reason' in answer && typeof answer.reason === 'string' ? { reason: answer.reason } : {}),
    ...('message' in answer && typeof answer.message === 'string'
      ? { message: answer.message }
      : {}),
    ...(answer.status === 'held' ? { hold_id: answer.hold_id } : {}),
  };
  await tx.execute('UPDATE batch_action SET status = ?, answer = ?, answered_at = ? WHERE id = ?', [
    answer.status,
    JSON.stringify(kept),
    now.toISOString(),
    id,
  ]);
}

/** Batch actions recorded on `day` (local date), newest first. */
export function readBatchActions(db: LocalReader, day: string): Promise<BatchActionRow[]> {
  return db.getAll<BatchActionRow>('SELECT * FROM batch_action WHERE day = ? ORDER BY at DESC', [
    day,
  ]);
}
